import { YANDEX_METRIKA_ID } from "@/shared/config/analytics-ids";

/**
 * Тонкая обёртка над аналитикой: Umami (self-hosted, cookieless) — всегда,
 * а Яндекс.Метрика (цель reachGoal) и GTM (событие dataLayer) — только если
 * пользователь дал согласие и их скрипты загружены (KAN-1198). Без согласия
 * `ym` и `dataLayer` на странице просто нет, и вызовы ниже — no-op.
 *
 * Зачем: Umami сам по себе видит только просмотры страниц — «сколько зашло».
 * Бизнес-события (регистрация на турнир) он не знает, а именно они дают воронку
 * «зашёл → зарегался» в его же интерфейсе, без отдельного дашборда.
 *
 * Безопасно вызывать откуда угодно: если скрипт не загружен (локальная разработка,
 * адблок, оффлайн) — тихий no-op, никаких исключений в бизнес-логике.
 */

type UmamiTrack = (
  eventName: string,
  eventData?: Record<string, string | number | boolean>,
) => void;

interface UmamiGlobal {
  track?: UmamiTrack;
}

export function trackEvent(
  name: string,
  data?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as {
      umami?: UmamiGlobal;
      ym?: (id: number, method: "reachGoal", goal: string, params?: object) => void;
      dataLayer?: Array<Record<string, unknown>>;
    };
    w.umami?.track?.(name, data);
    if (YANDEX_METRIKA_ID > 0) w.ym?.(YANDEX_METRIKA_ID, "reachGoal", name, data);
    // dataLayer создаёт только сниппет GTM, поэтому без согласия его нет —
    // и сами его не создаём, чтобы не копить события «на потом».
    w.dataLayer?.push({ event: name, ...data });
  } catch {
    // Аналитика никогда не должна ронять пользовательский сценарий.
  }
}

/** Имена событий держим в одном месте, чтобы в дашборде не расползались варианты. */
export const AnalyticsEvents = {
  /** Успешная регистрация участника на турнир. */
  TournamentRegistered: "tournament-registered",
  /** Клик по кнопке «Участвовать» (до прохождения проверок/логина). */
  TournamentRegisterClicked: "tournament-register-clicked",
  /** Успешная регистрация аккаунта по email (KAN-1198). */
  UserRegistered: "user-registered",
  /** Создана команда (цели Метрики, SEO-аудит 25.09.2026). */
  TeamCreated: "team-created",
  /** Создан турнир — с нуля или из шаблона. */
  TournamentCreated: "tournament-created",
  /** Отправлена заявка на организацию. */
  OrganizationApplied: "organization-applied",
} as const;
